import { useEffect, useState } from "react";
import { Loader2, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { getTimeline, getStreaks, STATUS_LABELS, STATUS_COLORS } from "../lib/api";
import StreaksBanner from "../components/StreaksBanner";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatMonth(key) {
  if (!key || key === "unknown") return "Unknown";
  const [y, m] = key.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_NAMES[idx] || m} ${y}`;
}

function formatDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return ""; }
}

export default function Timeline() {
  const [data, setData] = useState(null);
  const [streaks, setStreaks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([getTimeline(), getStreaks()]);
        setData(t);
        setStreaks(s);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;
  }

  if (!data || data.total === 0) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-10">
        <h1 className="font-display text-6xl uppercase text-white">Nothing tracked yet</h1>
        <p className="mt-3 text-neutral-500">Add anime and they&apos;ll appear here in chronological order.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 05</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Timeline
      </h1>
      <p className="mt-4 max-w-lg text-sm text-neutral-400">
        {data.total} entries, chronologically. Latest first.
      </p>

      {/* Streaks banner */}
      <div className="mt-10">
        <StreaksBanner streaks={streaks} />
      </div>

      <div className="mt-12 space-y-16">
        {data.groups.map((group) => (
          <section key={group.month} data-testid={`timeline-group-${group.month}`}>
            <div className="sticky top-16 z-20 flex items-baseline justify-between border-b border-white/15 bg-black/80 px-1 py-3 backdrop-blur-md">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-[#FF3B30]" />
                <h2 className="font-display text-3xl uppercase tracking-tight text-white md:text-4xl">
                  {formatMonth(group.month)}
                </h2>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                {group.entries.length} added
              </span>
            </div>

            <div className="mt-6 space-y-3">
              {group.entries.map((e, i) => (
                <Link
                  key={e.mal_id}
                  to={`/anime/${e.mal_id}`}
                  data-testid={`timeline-item-${e.mal_id}`}
                  className="group grid grid-cols-[60px_60px_1fr_auto] items-center gap-4 border border-white/10 bg-neutral-950 p-3 transition-colors hover:border-[#FF3B30]"
                  style={{ animation: `fadeUp 0.4s ${i * 0.03}s both ease-out` }}
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                    {formatDay(e.date_added)}
                  </div>
                  <div className="h-16 w-12 flex-shrink-0 overflow-hidden bg-neutral-900">
                    {e.image_url && <img src={e.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-white group-hover:text-[#FF3B30]">{e.title}</h3>
                    <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      {(e.genres || []).slice(0, 3).join(" · ")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${STATUS_COLORS[e.status] || "text-white border-white/30"}`}>
                      {STATUS_LABELS[e.status] || e.status}
                    </span>
                    {e.user_rating != null && (
                      <span className="border border-white/15 px-2 py-1 font-mono text-[9px] text-white">
                        ★ {Number(e.user_rating).toFixed(1)}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
